import clsx from 'clsx';
import { useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { useWishlist } from '~/features/commerce-api/hooks/wishlist/useWishList';
import { usePDPData } from '~/features/product-details/context';
import { _transformAlgoliaToProductDetails } from '~/features/productList/Algoliga.utils';
import { AlgoliaProductHit } from '~/lib/algolia/types/product';
import { useAlgoliaQuery } from '~/services/instantsearch/hooks';
import { useProductHandler } from '~/services/search/useProductHandler';
import { CardCarousel } from '~/shared/components/CardCarousel/CardCarousel';
import { NewProductCard } from '~/shared/components/NewProductCard';
import { TProductDetails } from '~/shared/components/ProductCard/ProductCard.definition';
import { _transformProductDetailsToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { useEvents } from '~/shared/hooks/useEvents/useEvents';
import { GTM_LIST_ID, GTM_LIST_NAME } from '~/shared/hooks/useEvents/useGTMEvents/models';
import { useUtils } from '~/shared/hooks/useUtils';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import { ModuleContainer } from '~/templates/blocks/components/ModuleContainer';
import { usePage } from '~/templates/pages';
import { theme } from '~/theme';
import styles from './RelatedProducts.module.scss';

type RelatedProductsProps = {
    isCondensed?: boolean;
};

export const RelatedProducts: React.FC<RelatedProductsProps> = ({ isCondensed }) => {
    const { data: frame } = useFrame();
    const { translate } = useTranslation();
    const { productCategoryLinks } = usePage();
    const { productsImpressionsEvent } = useEvents(frame);
    const { product: primaryProduct } = usePDPData();
    const { convertPriceToLocale } = useUtils();
    const productHandler = useProductHandler();
    const { handleFavourite, isFavorite } = useWishlist();
    const productsInSerieLink = productCategoryLinks?.series ?? {};
    const { isPlusMember } = useCustomer();

    const { inView, ref } = useInView({
        triggerOnce: true,
        threshold: 0,
        rootMargin: '0px 0px -500px 0px',
    });

    const productSeries = primaryProduct?.c_ProductSeries;
    const productBrand = primaryProduct?.brand;

    const filters = `cust_series: '${productSeries}' AND brand: '${productBrand}'`;

    const { data: relatedProducts } = useAlgoliaQuery<
        { hits: AlgoliaProductHit[] },
        TProductDetails[]
    >(
        { filters },
        {
            queryKey: ['Algolia', 'RelatedProducts', filters],
            select: (data) =>
                data.hits.map((hit) =>
                    _transformAlgoliaToProductDetails(hit, translate, convertPriceToLocale),
                ),
            enabled: !!(productSeries && productBrand),
        },
    );

    const productsToDisplay = useMemo(
        () => relatedProducts?.filter(({ id }) => id !== primaryProduct.id),
        [relatedProducts, primaryProduct.id],
    );

    useEffect(() => {
        if (!inView || !productsToDisplay?.length) return;

        productsImpressionsEvent(
            GTM_LIST_ID.PRODUCT_CAROUSEL,
            `${GTM_LIST_NAME.CAROUSEL} - ${translate('content.partOfSeries')}`,
            productsToDisplay.map((product, index) =>
                _transformProductDetailsToDataLayer(product, 1, index, isPlusMember),
            ),
        );
    }, [inView, productsToDisplay]);

    if (!productsToDisplay?.length) return null;

    return (
        <ModuleContainer
            ref={ref}
            backgroundColor={theme.colors.brownLight}
            spacingBottom={isCondensed ? 'small' : 'default'}
            spacingTop={isCondensed ? 'small' : 'default'}
        >
            <CardCarousel
                isCondensed={isCondensed}
                callToAction={{ ...productsInSerieLink, text: translate('plp.seeAll') }}
                headlineVariant={isCondensed ? 'title-XS' : undefined}
                headline={{
                    tag: 'h2',
                    text: translate('pdp.productsInSerie'),
                }}
                items={productsToDisplay.map((product, index) => (
                    <NewProductCard
                        className={clsx(styles.productCard, isCondensed && styles.condensed)}
                        key={product.id}
                        product={product}
                        position={index}
                        trackingListId={translate('content.partOfSeries')}
                        trackingListName={translate('content.partOfSeries')}
                        onButtonClick={productHandler}
                        onFavourite={handleFavourite}
                        isFavourite={isFavorite(product.id)}
                        variant="opaque"
                        layout={isCondensed ? 'condensed' : 'default'}
                    />
                ))}
            />
        </ModuleContainer>
    );
};
